// services/movimientoCajaWebSocketService.ts

type EventCallback = (data: any) => void;

class MovimientoCajaWebSocketService {
    private ws: WebSocket | null = null;
    private reconnectAttempts = 0;
    private maxReconnectAttempts = 5;
    private reconnectDelay = 3000;
    private listeners: Map<string, Set<EventCallback>> = new Map();
    private connectionStatusListeners: Set<(status: boolean) => void> = new Set();
    private isConnected = false;
    private intentionalClose = false; // Para distinguir cierre intencional
    // Sesión POS EXPLÍCITA del canal: el backend no infiere ninguna caja
    // (compartida entre responsables) y cierra el socket sin ``sesion_id``.
    private sessionId: number | null = null;

    getSessionId(): number | null {
        return this.sessionId;
    }

    connect(sesionId?: number | null) {
        if (!Number.isInteger(sesionId) || (sesionId as number) <= 0) {
            /* Browser diagnostics omitted. */
            return;
        }
        // Cambio de sesión: se cierra el canal anterior antes de abrir el nuevo.
        if (this.ws && this.sessionId !== sesionId) {
            this.disconnect();
        }
        // Evitar múltiples conexiones simultáneas
        if (this.ws && (this.ws.readyState === WebSocket.CONNECTING || this.ws.readyState === WebSocket.OPEN)) {
            /* Browser diagnostics omitted. */
            return;
        }
        this.sessionId = sesionId as number;

        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const backendHost = process.env.REACT_APP_BACKEND_URL?.replace(/^https?:\/\//, '');
        const wsUrl = `${protocol}//${backendHost}/ws/movimientos-caja/?sesion_id=${this.sessionId}`;

        /* Browser diagnostics omitted. */
        this.intentionalClose = false;
        this.ws = new WebSocket(wsUrl);

        this.ws.onopen = () => {
            /* Browser diagnostics omitted. */
            this.isConnected = true;
            this.reconnectAttempts = 0;
            this.notifyConnectionStatus(true);
        };

        this.ws.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);
                /* Browser diagnostics omitted. */
                this.notifyListeners('movimiento_caja_actualizado', data);
            } catch (error) {
                /* Browser diagnostics omitted. */
            }
        };

        this.ws.onclose = (event) => {
            /* Browser diagnostics omitted. */
            this.isConnected = false;
            this.notifyConnectionStatus(false);
            this.ws = null;

            // Solo reintentar si no fue un cierre intencional
            if (!this.intentionalClose) {
                this.attemptReconnect();
            }
        };

        this.ws.onerror = (error) => {
            /* Browser diagnostics omitted. */
            // No cerrar manualmente aquí, onclose se encargará
        };
    }

    private attemptReconnect() {
        if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            /* Browser diagnostics omitted. */
            return;
        }
        setTimeout(() => {
            if (this.intentionalClose || this.sessionId === null) return;
            /* Browser diagnostics omitted. */
            this.reconnectAttempts++;
            this.connect(this.sessionId);
        }, this.reconnectDelay);
    }

    disconnect() {
        this.intentionalClose = true;
        const socket = this.ws;
        this.ws = null;
        this.sessionId = null;
        if (socket) {
            socket.onclose = null;
            socket.onmessage = null;
            socket.onerror = null;
            socket.close();
        }
        this.isConnected = false;
        this.notifyConnectionStatus(false);
        this.reconnectAttempts = 0; // Reiniciar contador
    }

    on(event: string, callback: EventCallback) {
        if (!this.listeners.has(event)) {
            this.listeners.set(event, new Set());
        }
        this.listeners.get(event)!.add(callback);
        /* Browser diagnostics omitted. */
        return () => this.off(event, callback);
    }

    off(event: string, callback: EventCallback) {
        this.listeners.get(event)?.delete(callback);
    }

    private notifyListeners(event: string, data: any) {
        const callbacks = this.listeners.get(event);
        /* Browser diagnostics omitted. */
        callbacks?.forEach((cb) => cb(data));
    }

    onConnectionStatus(callback: (connected: boolean) => void) {
        this.connectionStatusListeners.add(callback);
        callback(this.isConnected);
        return () => this.connectionStatusListeners.delete(callback);
    }

    private notifyConnectionStatus(connected: boolean) {
        this.connectionStatusListeners.forEach((cb) => cb(connected));
    }

    getConnected(): boolean {
        return this.isConnected;
    }
}

export const movimientoCajaWebSocketService = new MovimientoCajaWebSocketService();